import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Ensure cookies are sent with every request
axios.defaults.withCredentials = true;
const API_BASE = "https://api.zapexpenses.com";

import DeleteConfirmationModal from './DeleteConfirmationModal';
import { CATEGORY_OPTIONS } from './constants';

// Helper to create a unique key for each item
const getItemKey = (item) => `${item.type}-${item.id}${item.type === 'receipt' ? `-${item.receipt_index}` : ''}`;

const ReviewPage = () => {
    const [reviewItems, setReviewItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [imageUrls, setImageUrls] = useState({}); // id -> blobURL (for receipts)
    const [loadingImages, setLoadingImages] = useState({}); // id -> bool
    const [selectedImage, setSelectedImage] = useState(null); // Receipt object for modal
    const [isZoomed, setIsZoomed] = useState(false); // Zoom state for modal

    // Delete functionality
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Parse URL query parameter for single-item filtering
    const searchParams = new URLSearchParams(window.location.search);
    const filterId = searchParams.get('id');
    const filterType = searchParams.get('type') || 'receipt'; // Default to receipt if not specified
    const filterIndex = searchParams.get('index'); // Optional receipt_index

    // Reset zoom when opening a new image
    useEffect(() => {
        if (selectedImage) setIsZoomed(false);
    }, [selectedImage]);

    // Handle History/Back Button for Lightbox
    useEffect(() => {
        if (selectedImage) {
            window.history.pushState({ lightbox: true }, '');
            const handlePopState = () => {
                setSelectedImage(null);
                setIsZoomed(false);
            };
            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        }
    }, [selectedImage]);

    // 1. Fetch Data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            let receiptsData = [];
            let mileageData = [];

            if (filterId) {
                // Fetch specific item regardless of status
                if (filterType === 'receipt') {
                    // Try to fetch specific receipt. If backend supports it, great. 
                    // If not, we might need to fetch all and filter, or use a specific endpoint.
                    // Assuming /receipts?id=... works or we fetch all and filter.
                    // Verification via ReceiptsPage suggests /receipts?id=X works or /receipts returns all.
                    // Let's try fetching all for now if no specific endpoint, or use the query param if supported.
                    // ReceiptsPage uses: endpoint = filterId ? `${API_BASE}/receipts?id=${filterId}` : ...
                    const res = await axios.get(`${API_BASE}/receipts?id=${filterId}`);
                    // Ensure we get an array
                    receiptsData = Array.isArray(res.data) ? res.data : [res.data];
                } else {
                    // Mileage - fetch all and filter since we don't know if ?id= works for sure, 
                    // but MileagePage fetches all. 
                    const res = await axios.get(`${API_BASE}/mileage_logs`);
                    mileageData = res.data.filter(m => String(m.id) === filterId);
                }
            } else {
                // Default: Fetch only pending
                const [receiptsRes, mileageRes] = await Promise.all([
                    axios.get(`${API_BASE}/receipts?status=pending`),
                    axios.get(`${API_BASE}/mileage_logs`)
                ]);
                receiptsData = receiptsRes.data;
                mileageData = mileageRes.data.filter(m => m.status === 'pending');
            }

            // Normalize Receipts
            const receipts = receiptsData.map(r => ({
                ...r,
                type: 'receipt',
                sortDate: new Date(r.created_at || r.date),
                values: r.values || {
                    date: r.date,
                    merchant_name: r.merchant_name,
                    category: r.category,
                    total: r.total,
                    currency: r.currency,
                    vat: r.vat
                }
            }));

            // Normalize Mileage
            const mileage = mileageData.map(m => ({
                ...m,
                type: 'mileage',
                sortDate: new Date(m.date),
                values: {
                    date: m.date,
                    origin: m.origin,
                    destination: m.destination,
                    distance: m.distance || m.miles,
                    vehicle: m.vehicle,
                    rate: m.rate,
                    purpose: m.purpose
                }
            }));

            // Combine and Sort (Ascending: Oldest First)
            const combined = [...receipts, ...mileage].sort((a, b) => a.sortDate - b.sortDate);

            setReviewItems(combined);
            setMessage('');
        } catch (err) {
            console.error(err);
            setMessage('Failed to load items. Please try reloading.');
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch images for receipts
    useEffect(() => {
        reviewItems.forEach(item => {
            if (item.type === 'receipt' && !imageUrls[item.id] && !loadingImages[item.id]) {
                fetchImage(item);
            }
        });
    }, [reviewItems]);

    const fetchImage = async (receipt) => {
        setLoadingImages(prev => ({ ...prev, [receipt.id]: true }));
        try {
            const res = await axios.get(`${API_BASE}/receipts/image/${receipt.id}`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            setImageUrls(prev => ({ ...prev, [receipt.id]: url }));
        } catch (err) {
            console.error("Failed to fetch image", err);
        } finally {
            setLoadingImages(prev => ({ ...prev, [receipt.id]: false }));
        }
    };

    // Helper for "Receipt X of Y"
    const getReceiptCountForId = (id) => reviewItems.filter(i => i.type === 'receipt' && i.id === id).length;
    const getReceiptPositionForId = (receipt) => {
        const sameIdReceipts = reviewItems.filter(i => i.type === 'receipt' && i.id === receipt.id);
        return sameIdReceipts.findIndex(r => r.receipt_index === receipt.receipt_index) + 1;
    };

    // 2. Handle Input Change
    const handleInputChange = (item, field, value) => {
        setReviewItems(prevItems => prevItems.map(i => {
            const isMatch = i.type === item.type && i.id === item.id && (item.type !== 'receipt' || i.receipt_index === item.receipt_index);
            if (isMatch) {
                return {
                    ...i,
                    values: { ...i.values, [field]: value }
                };
            }
            return i;
        }));
    };

    // 3. Submit Fix
    const submitFix = async (item) => {
        try {
            if (item.type === 'receipt') {
                const url = `${API_BASE}/receipts/fix/${item.id}/${item.receipt_index ?? 0}`;
                await axios.post(url, item.values);
            } else {
                // Mileage Fix
                // Need to map values back to expected backend format if needed, 
                // but checking MileagePage, it sends `editValues` directly to `/mileage_logs/fix/:id`
                // Ensure field names match what backend expects.
                // MileagePage sends: origin, destination, purpose, miles, date, vehicle, rate.
                // Our values object has these.
                await axios.post(`${API_BASE}/mileage_logs/fix/${item.id}`, item.values);
            }

            // Remove from list
            setReviewItems(prev => prev.filter(i =>
                !(i.type === item.type && i.id === item.id && (item.type !== 'receipt' || i.receipt_index === item.receipt_index))
            ));
        } catch (err) {
            console.error(err);
            const errorMsg = err.response?.data?.detail || err.message || "Unknown error";
            alert(`Failed to save changes. Error: ${errorMsg}`);
        }
    };

    // 4. Delete Logic
    const checkDelete = (item) => {
        setItemToDelete(item);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            if (itemToDelete.type === 'receipt') {
                const url = `${API_BASE}/receipts/delete/${itemToDelete.id}/${itemToDelete.receipt_index ?? 0}`;
                await axios.post(url);
            } else {
                // Mileage Delete
                await axios.delete(`${API_BASE}/mileage_logs/${itemToDelete.id}`);
            }

            // Remove from list
            setReviewItems(prev => prev.filter(i =>
                !(i.type === itemToDelete.type && i.id === itemToDelete.id && (itemToDelete.type !== 'receipt' || i.receipt_index === itemToDelete.receipt_index))
            ));

            setDeleteModalOpen(false);
            setItemToDelete(null);

            // If we were filtering by this specific item ID, redirect to main review
            if (filterId && String(itemToDelete.id) === filterId) {
                window.location.href = '/review';
            }

        } catch (err) {
            console.error("Delete failed", err);
            alert("Failed to delete item. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const RECEIPT_LABELS = {
        total: "Total Amount",
        vat: "VAT Amount",
        merchant_name: "Merchant",
        category: "Category",
        date: "Date",
        currency: "Currency"
    };

    const MILEAGE_LABELS = {
        date: "Date",
        origin: "Origin",
        destination: "Destination",
        distance: "Distance (miles)",
        vehicle: "Vehicle",
        rate: "Rate (£/mi)",
        purpose: "Purpose"
    };

    // Redirect when review is complete for a specific item
    useEffect(() => {
        if (!loading && filterId && reviewItems.length === 0) {
            // Short delay to allow user to see the item disappear or a success state if we had one
            const timer = setTimeout(() => {
                if (filterType === 'mileage') {
                    window.location.href = '/mileage';
                } else {
                    window.location.href = '/receipts';
                }
            }, 500); // 500ms delay
            return () => clearTimeout(timer);
        }
    }, [loading, filterId, reviewItems, filterType]);

    if (loading) return <div className="p-8 text-center text-slate-400">Loading items...</div>;

    // Use a specific "Completed" message if valid redirect is pending
    if (filterId && reviewItems.length === 0) {
        return (
            <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-xl max-w-lg mx-auto mt-10">
                <div className="text-4xl mb-4">✅</div>
                <h2 className="text-xl font-bold text-white mb-2">Review Complete</h2>
                <p className="text-slate-400 text-sm">Redirecting back to list...</p>
            </div>
        );
    }

    // Filter Logic
    const filteredItems = reviewItems.filter(item => {
        if (!filterId) return true;
        // Strict filtering if ID is present
        if (String(item.id) !== filterId) return false;
        if (filterType && item.type !== filterType) return false;
        if (filterIndex !== null && item.type === 'receipt' && String(item.receipt_index) !== filterIndex) return false;
        return true;
    });

    return (
        <div className={`max-w-4xl mx-auto p-6 ${filterId ? 'pb-32' : ''}`}>
            <h1 className="text-2xl font-bold mb-6">Review Expenses</h1>

            {message && <p className="text-red-500 mb-4">{message}</p>}

            {reviewItems.length === 0 && !message ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-200 p-6 rounded-xl text-center">
                    🎉 All caught up! No items need review.
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Filter Context Banner */}
                    {filterId && (
                        <div className="mb-4 bg-slate-900/50 p-4 rounded-lg border border-indigo-500/30 text-center">
                            <span className="text-indigo-200 text-sm">
                                Reviewing specific {filterType} (ID: {filterId})
                            </span>
                        </div>
                    )}

                    {filteredItems.map(item => {
                        // Common props
                        const key = getItemKey(item);
                        const isReceipt = item.type === 'receipt';

                        if (isReceipt) {
                            // --- RECEIPT CARD ---
                            const totalForId = getReceiptCountForId(item.id);
                            const position = getReceiptPositionForId(item);
                            const showMultiIndicator = totalForId > 1;

                            const DEFAULT_FIELDS = ['date', 'merchant_name', 'category', 'total', 'vat', 'currency'];
                            const hasValues = item.values && Object.keys(item.values).length > 0;
                            const fieldsToRender = hasValues ? Object.keys(item.values) : DEFAULT_FIELDS;

                            return (
                                <div key={key} className="border border-slate-800 rounded-xl p-6 bg-slate-900 shadow-sm flex flex-col md:flex-row gap-6 items-start">
                                    <div className="flex-1">
                                        <p className="text-sm text-slate-500 mb-1">
                                            Receipt • {new Date(item.created_at).toLocaleDateString()} • {item.source}
                                        </p>
                                        <div className="p-3 bg-black/30 rounded border border-white/5 font-mono text-xs text-slate-400 break-all mb-4">
                                            ID: {item.id}
                                            {showMultiIndicator && (
                                                <span className="ml-2 px-2 py-0.5 bg-indigo-600/30 text-indigo-300 rounded text-xs">
                                                    Receipt {position} of {totalForId}
                                                </span>
                                            )}
                                        </div>

                                        {/* Image Thumbnail */}
                                        <div className="rounded-lg overflow-hidden border border-slate-700 bg-black/50 relative flex items-center justify-center transition-all duration-300 group">
                                            {loadingImages[item.id] ? (
                                                <div className="text-slate-500 text-xs animate-pulse p-4">Loading image...</div>
                                            ) : imageUrls[item.id] ? (
                                                <div className="relative cursor-zoom-in w-full" onClick={() => setSelectedImage(item)}>
                                                    <img
                                                        src={imageUrls[item.id]}
                                                        alt="Receipt"
                                                        className="w-full max-h-64 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                                        <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">Click to Zoom</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-red-400 text-xs p-4">Image unavailable</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-3 w-full">
                                        <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wide mb-2">Needs Verification</h3>
                                        {!hasValues && <div className="text-xs text-amber-500 mb-2">No data extracted. Please enter details manually.</div>}
                                        {fieldsToRender.map(field => (
                                            <div key={field}>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">
                                                    {RECEIPT_LABELS[field] || field}
                                                </label>
                                                {field === 'category' ? (
                                                    <select
                                                        value={item.values?.[field] || ''}
                                                        onChange={(e) => handleInputChange(item, field, e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                    >
                                                        <option value="" disabled>Select Category...</option>
                                                        {CATEGORY_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type={field.includes('date') ? 'date' : 'text'}
                                                        value={item.values?.[field] ?? ''}
                                                        onChange={(e) => handleInputChange(item, field, e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                    />
                                                )}
                                            </div>
                                        ))}

                                        <div className="grid grid-cols-2 gap-3 mt-4">
                                            <button
                                                onClick={() => submitFix(item)}
                                                className="col-span-2 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition font-semibold"
                                            >
                                                Approve &amp; Save
                                            </button>
                                            <button
                                                onClick={() => checkDelete(item)}
                                                className="col-span-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 py-2 rounded transition-colors flex items-center justify-center gap-1.5"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                </svg>
                                                Discard / Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        } else {
                            // --- MILEAGE CARD ---
                            // Similar layout but specialized for mileage fields
                            return (
                                <div key={key} className="border border-slate-800 rounded-xl p-6 bg-slate-900 shadow-sm flex flex-col gap-6">
                                    <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                                        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center border border-white/5 text-lg">
                                            🚗
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-white">Mileage Log</p>
                                            <p className="text-xs text-slate-500">
                                                Created {new Date(item.created_at || item.date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Date & Vehicle */}
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Date</label>
                                                <input
                                                    type="date"
                                                    value={item.values?.date ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'date', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Vehicle</label>
                                                <input
                                                    type="text"
                                                    value={item.values?.vehicle ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'vehicle', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>

                                            {/* Origin & Destination */}
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Origin</label>
                                                <input
                                                    type="text"
                                                    value={item.values?.origin ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'origin', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Destination</label>
                                                <input
                                                    type="text"
                                                    value={item.values?.destination ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'destination', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>

                                            {/* Distance & Rate */}
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Distance (miles)</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={item.values?.distance ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'distance', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Rate (£/mi)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={item.values?.rate ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'rate', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>

                                            {/* Purpose */}
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">Purpose</label>
                                                <input
                                                    type="text"
                                                    value={item.values?.purpose ?? ''}
                                                    onChange={(e) => handleInputChange(item, 'purpose', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-3 mt-4">
                                            <button
                                                onClick={() => submitFix(item)}
                                                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition font-semibold"
                                            >
                                                Approve &amp; Save
                                            </button>
                                            <button
                                                onClick={() => checkDelete(item)}
                                                className="px-3 py-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                                title="Delete Log"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    })}
                </div>
            )}

            {/* Bottom Tab for Review All */}
            {filterId && (
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/90 backdrop-blur border-t border-slate-800 flex justify-center z-40">
                    <div className="max-w-4xl w-full flex justify-between items-center px-2">
                        <span className="text-slate-400 text-sm hidden sm:block">Viewing single item</span>
                        <button
                            onClick={() => window.location.href = '/review'}
                            className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-500 transition shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
                        >
                            <span>Review All Pending ({reviewItems.length})</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Lightbox Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-in fade-in duration-200"
                    onClick={() => window.history.back()}
                >
                    <button
                        className="fixed top-4 right-4 bg-black/50 text-white p-2 z-[60] rounded-full hover:bg-black/70 backdrop-blur-md transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.history.back();
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className={`w-full h-full overflow-auto p-4 ${isZoomed ? '' : 'flex items-center justify-center'}`}>
                        <img
                            src={imageUrls[selectedImage.id]}
                            alt="Zoomed Receipt"
                            className={`rounded shadow-2xl transition-all duration-300 ${isZoomed
                                ? 'w-full max-w-none cursor-zoom-out'
                                : 'max-h-full max-w-full object-contain cursor-zoom-in'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsZoomed(!isZoomed);
                            }}
                        />
                    </div>

                    <div className="fixed bottom-6 left-0 right-0 text-center pointer-events-none z-50">
                        <span className="bg-black/70 text-white/90 text-sm px-4 py-2 rounded-full backdrop-blur-md border border-white/10 shadow-lg">
                            Captured on {new Date(selectedImage.created_at).toLocaleDateString()} by {selectedImage.source}
                        </span>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
                title="Discard Item?"
                message="Are you sure you want to discard this item? It will be removed from your list."
            />
        </div >
    );
};

export default ReviewPage;